import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Package,
  ShoppingCart,
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  TrendingDown,
  RefreshCw,
  Save,
  Layers,
  Zap,
  Boxes,
} from "lucide-react";
import { API } from "@/lib/auth";

const WINDOWS = [
  { k: "24h", l: "Last 24h" },
  { k: "7d",  l: "Last 7 days" },
  { k: "30d", l: "Last 30 days" },
];

export default function InventoryFunnelDashboard() {
  const [win, setWin] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingStock, setEditingStock] = useState({}); // slug -> value
  const [saving, setSaving] = useState({}); // slug -> bool
  const [filter, setFilter] = useState("all"); // all | in_baskets | low_stock | dropping

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/inventory-funnel?window=${win}`);
      setData(r.data);
      setEditingStock({});
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [win]);

  useEffect(() => { load(); }, [load]);

  const saveStock = async (slug) => {
    const raw = editingStock[slug];
    if (raw === undefined) return;
    const value = raw === "" || raw === null ? null : parseInt(raw, 10);
    if (value !== null && (isNaN(value) || value < 0)) {
      toast.error("Stock must be 0 or greater");
      return;
    }
    setSaving((s) => ({ ...s, [slug]: true }));
    try {
      await axios.patch(`${API}/admin/products/${slug}`, { stock: value });
      toast.success(value === null ? "Stock set to unlimited" : `Stock set to ${value}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [slug]: false }));
    }
  };

  const filteredProducts = useMemo(() => {
    const list = data?.products || [];
    if (filter === "in_baskets") return list.filter((p) => p.in_active_baskets > 0);
    if (filter === "low_stock") return list.filter((p) => p.is_low_stock || p.is_out_of_stock);
    if (filter === "dropping") {
      return [...list].filter((p) => p.checkouts_started > 0).sort((a, b) => b.drop_off_rate - a.drop_off_rate);
    }
    return list;
  }, [data, filter]);

  if (!data && loading) {
    return <div className="p-16 text-center text-[hsl(var(--onyx)/0.5)]">Crunching numbers…</div>;
  }
  if (!data) {
    return <div className="p-16 text-center text-[hsl(var(--onyx)/0.5)]">No data yet.</div>;
  }

  const f = data.funnel;

  return (
    <div className="space-y-6" data-testid="inventory-funnel-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="overline">Inventory &amp; Checkout Funnel</div>
          <p className="text-sm text-[hsl(var(--onyx)/0.6)] mt-1 max-w-xl">
            Live basket snapshot, per-product drop-off, and low-stock alerts. Edit any stock count inline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map((w) => (
            <button
              key={w.k}
              onClick={() => setWin(w.k)}
              data-testid={`invfn-window-${w.k}`}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
                win === w.k
                  ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                  : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx)/0.3)]"
              }`}
            >
              {w.l}
            </button>
          ))}
          <button
            onClick={load}
            data-testid="invfn-refresh"
            className="btn-pill btn-outline !text-xs"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Funnel row */}
      <FunnelStrip f={f} />

      {/* Low stock + top abandoned */}
      <div className="grid gap-5 lg:grid-cols-2">
        <LowStockCard rows={data.low_stock_alerts} onJump={(slug) => document.getElementById(`invfn-row-${slug}`)?.scrollIntoView({ behavior: "smooth", block: "center" })} />
        <TopAbandonedCard rows={data.top_abandoned} onJump={(slug) => document.getElementById(`invfn-row-${slug}`)?.scrollIntoView({ behavior: "smooth", block: "center" })} />
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2" data-testid="invfn-filter-tabs">
        {[
          { k: "all", l: `All products (${data.products.length})` },
          { k: "in_baskets", l: `In baskets now (${data.products.filter(p => p.in_active_baskets > 0).length})` },
          { k: "low_stock", l: `Low stock (${data.low_stock_alerts.length})` },
          { k: "dropping", l: "Worst drop-off" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={`invfn-filter-${t.k}`}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${
              filter === t.k
                ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx)/0.3)]"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* Product table */}
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] overflow-hidden">
        <div className="grid grid-cols-[1.6fr_0.9fr_0.7fr_0.8fr_0.8fr_0.8fr_0.7fr] gap-3 px-5 py-3 border-b border-[hsl(var(--onyx)/0.06)] text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)] bg-[hsl(var(--blush)/0.15)]">
          <div>Product</div>
          <div>Stock</div>
          <div>In baskets now (24h)</div>
          <div>Carts ({win})</div>
          <div>Checkouts</div>
          <div>Completed</div>
          <div className="text-right">Drop-off</div>
        </div>

        <div className="max-h-[720px] overflow-y-auto" data-testid="invfn-product-list">
          {filteredProducts.length === 0 ? (
            <div className="py-16 text-center text-[hsl(var(--onyx)/0.45)] text-sm">
              No products match this filter.
            </div>
          ) : (
            filteredProducts.map((p) => (
              <ProductRow
                key={p.slug}
                p={p}
                edited={editingStock[p.slug]}
                saving={!!saving[p.slug]}
                onChange={(v) => setEditingStock((s) => ({ ...s, [p.slug]: v }))}
                onSave={() => saveStock(p.slug)}
                onCancel={() =>
                  setEditingStock((s) => {
                    const n = { ...s };
                    delete n[p.slug];
                    return n;
                  })
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Funnel visualisation ============
function FunnelStrip({ f }) {
  // Compute stage percentages relative to the top of funnel
  const top = Math.max(f.cart_created, f.checkout_started, 1);
  const stages = [
    { k: "cart_created", l: "Carts created", v: f.cart_created, Icon: ShoppingCart, hint: "Baskets started in window" },
    { k: "checkout_started", l: "Checkout started", v: f.checkout_started, Icon: CreditCard, hint: "Clicked Pay → Stripe session" },
    { k: "completed", l: "Completed", v: f.completed, Icon: CheckCircle2, hint: `Conversion ${f.conversion_rate}%`, highlight: true },
    { k: "checkout_expired", l: "Expired / abandoned", v: f.checkout_expired, Icon: TrendingDown, hint: "Started but never paid", muted: true },
  ];
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5" data-testid="invfn-funnel">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">Checkout funnel</div>
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--onyx)/0.6)]">
          <Zap size={13} className="text-[hsl(var(--gold))]" />
          <span data-testid="invfn-live-baskets">
            <strong>{f.live_active_baskets}</strong> live active basket{f.live_active_baskets === 1 ? "" : "s"} (last 24h)
          </span>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        {stages.map(({ k, l, v, Icon, hint, highlight, muted }) => {
          const pct = Math.round((v / top) * 100);
          return (
            <div
              key={k}
              data-testid={`invfn-stage-${k}`}
              className={`relative rounded-2xl p-4 overflow-hidden border ${
                highlight
                  ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] border-[hsl(var(--onyx))]"
                  : muted
                  ? "bg-[hsl(var(--blush)/0.2)] border-[hsl(var(--onyx)/0.06)]"
                  : "bg-[hsl(var(--cream))] border-[hsl(var(--onyx)/0.06)]"
              }`}
            >
              <div
                aria-hidden
                className={`absolute inset-y-0 left-0 ${
                  highlight ? "bg-[hsl(var(--gold)/0.3)]" : "bg-[hsl(var(--blush)/0.5)]"
                }`}
                style={{ width: `${pct}%`, transition: "width 400ms ease" }}
              />
              <div className="relative">
                <div className={`flex items-center gap-2 ${highlight ? "text-[hsl(var(--gold))]" : "text-[hsl(var(--gold))]"}`}>
                  <Icon size={14} />
                  <span className="overline">{l}</span>
                </div>
                <div className="font-display text-4xl mt-2">{v}</div>
                <div className={`text-[11px] mt-1 ${highlight ? "text-[hsl(var(--cream)/0.7)]" : "text-[hsl(var(--onyx)/0.55)]"}`}>
                  {hint}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Low-stock alerts ============
function LowStockCard({ rows, onJump }) {
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5" data-testid="invfn-low-stock">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={14} className="text-[hsl(var(--burgundy))]" />
        <div className="overline text-[hsl(var(--burgundy))]">Low stock &amp; out-of-stock</div>
      </div>
      {rows.length === 0 ? (
        <div className="py-6 text-sm text-[hsl(var(--onyx)/0.5)]">
          Everything looks well-stocked. Nice.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <button
              key={r.slug}
              onClick={() => onJump(r.slug)}
              className="w-full text-left flex items-center gap-3 rounded-2xl bg-[hsl(var(--blush)/0.2)] p-3 hover:bg-[hsl(var(--blush)/0.4)] transition-colors"
              data-testid={`invfn-low-stock-${r.slug}`}
            >
              {r.image ? (
                <img src={r.image} alt="" className="w-11 h-11 rounded-xl object-cover" />
              ) : (
                <div className="w-11 h-11 rounded-xl bg-[hsl(var(--onyx)/0.05)]" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{r.name}</div>
                <div className="text-[11px] text-[hsl(var(--onyx)/0.6)]">
                  {r.is_out_of_stock
                    ? "Out of stock"
                    : `${r.stock} left (threshold ${r.low_stock_threshold})`}
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                r.is_out_of_stock ? "bg-[hsl(var(--burgundy))] text-white" : "bg-[hsl(var(--gold)/0.2)] text-[hsl(var(--gold))]"
              }`}>
                {r.is_out_of_stock ? "0 left" : "Low"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ Top abandoned products ============
function TopAbandonedCard({ rows, onJump }) {
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5" data-testid="invfn-top-abandoned">
      <div className="flex items-center gap-2 mb-3">
        <TrendingDown size={14} className="text-[hsl(var(--gold))]" />
        <div className="overline">Most-abandoned products</div>
      </div>
      {rows.length === 0 ? (
        <div className="py-6 text-sm text-[hsl(var(--onyx)/0.5)]">
          No abandoned checkouts in this window.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.slice(0, 6).map((r) => {
            const lost = r.checkouts_started - r.checkouts_completed;
            return (
              <button
                key={r.slug}
                onClick={() => onJump(r.slug)}
                className="w-full text-left flex items-center gap-3 rounded-2xl bg-[hsl(var(--cream))] p-3 hover:bg-[hsl(var(--blush)/0.35)] transition-colors"
                data-testid={`invfn-abandoned-${r.slug}`}
              >
                {r.image ? (
                  <img src={r.image} alt="" className="w-11 h-11 rounded-xl object-cover" />
                ) : (
                  <div className="w-11 h-11 rounded-xl bg-[hsl(var(--onyx)/0.05)]" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  <div className="text-[11px] text-[hsl(var(--onyx)/0.6)]">
                    {r.checkouts_started} started · {r.checkouts_completed} paid · <strong className="text-[hsl(var(--burgundy))]">{lost} lost</strong>
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[hsl(var(--burgundy)/0.1)] text-[hsl(var(--burgundy))]">
                  {r.drop_off_rate}% off
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ Product row ============
function ProductRow({ p, edited, saving, onChange, onSave, onCancel }) {
  const isEditing = edited !== undefined;
  const stockDisplay = p.digital
    ? <span className="text-[hsl(var(--onyx)/0.4)] italic">digital · ∞</span>
    : p.stock === null || p.stock === undefined
      ? <span className="text-[hsl(var(--onyx)/0.4)] italic">untracked</span>
      : <span className={p.is_out_of_stock ? "text-[hsl(var(--burgundy))] font-medium" : p.is_low_stock ? "text-[hsl(var(--gold))] font-medium" : ""}>{p.stock}</span>;

  return (
    <div
      id={`invfn-row-${p.slug}`}
      data-testid={`invfn-row-${p.slug}`}
      className={`grid grid-cols-[1.6fr_0.9fr_0.7fr_0.8fr_0.8fr_0.8fr_0.7fr] gap-3 px-5 py-4 border-b border-[hsl(var(--onyx)/0.05)] text-sm items-center transition-colors hover:bg-[hsl(var(--blush)/0.1)] ${
        p.is_out_of_stock ? "bg-[hsl(var(--burgundy)/0.04)]" : ""
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {p.image ? (
          <img src={p.image} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
        ) : (
          <Boxes size={20} className="text-[hsl(var(--onyx)/0.3)]" />
        )}
        <div className="min-w-0">
          <div className="font-medium truncate">{p.name}</div>
          <div className="text-[11px] text-[hsl(var(--onyx)/0.5)] truncate">
            {p.category} · £{Number(p.price).toFixed(2)}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 min-w-0">
        {isEditing ? (
          <>
            <input
              type="number"
              min="0"
              value={edited === null ? "" : edited}
              onChange={(e) => onChange(e.target.value)}
              data-testid={`invfn-stock-input-${p.slug}`}
              placeholder="∞"
              className="w-16 h-8 rounded-lg bg-white border border-[hsl(var(--onyx)/0.2)] px-2 text-sm outline-none focus:border-[hsl(var(--onyx))]"
            />
            <button
              onClick={onSave}
              disabled={saving || p.digital}
              data-testid={`invfn-stock-save-${p.slug}`}
              className="w-7 h-7 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] flex items-center justify-center disabled:opacity-40"
            >
              <Save size={12} />
            </button>
            <button
              onClick={onCancel}
              className="text-[11px] text-[hsl(var(--onyx)/0.5)] underline"
            >
              cancel
            </button>
          </>
        ) : (
          <button
            onClick={() => onChange(p.stock === null || p.stock === undefined ? "" : String(p.stock))}
            disabled={p.digital}
            data-testid={`invfn-stock-edit-${p.slug}`}
            className="inline-flex items-center gap-2 rounded-full px-2 py-1 hover:bg-white transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
            title={p.digital ? "Digital product — always in stock" : "Click to edit"}
          >
            {stockDisplay}
            {!p.digital && (
              <span className="text-[10px] text-[hsl(var(--onyx)/0.4)] uppercase tracking-widest">edit</span>
            )}
          </button>
        )}
      </div>

      <div>
        {p.in_active_baskets > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--gold)/0.15)] text-[hsl(var(--gold))] px-2 py-0.5 text-xs">
            <Layers size={11} /> {p.in_active_baskets}
          </span>
        ) : (
          <span className="text-[hsl(var(--onyx)/0.3)]">—</span>
        )}
      </div>

      <div className="text-[hsl(var(--onyx)/0.75)]">{p.carts_in_window}</div>
      <div>{p.checkouts_started}</div>
      <div className="text-[hsl(var(--gold))] font-medium">{p.checkouts_completed}</div>
      <div className="text-right">
        {p.checkouts_started > 0 ? (
          <span className={`text-xs font-medium ${p.drop_off_rate >= 70 ? "text-[hsl(var(--burgundy))]" : "text-[hsl(var(--onyx)/0.7)]"}`}>
            {p.drop_off_rate}%
          </span>
        ) : (
          <span className="text-[hsl(var(--onyx)/0.3)]">—</span>
        )}
      </div>
    </div>
  );
}
